import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import type { UserRole } from "./types";

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Redirects to /login if the user is not authenticated.
 * Does NOT enforce password change — RequirePasswordChanged handles that
 * so the /force-password-change route itself can sit behind RequireAuth.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner />;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * Wraps protected app routes. If user.must_change_password is true,
 * routes them to /force-password-change. Otherwise renders children.
 */
export function RequirePasswordChanged({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.must_change_password) {
    return <Navigate to="/force-password-change" replace />;
  }
  return <>{children}</>;
}

/**
 * Allows only users whose `role` matches one of `roles`.
 * Renders a 403 fallback otherwise.
 */
export function RequireRole({ roles, children }: { roles: UserRole[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null; // RequireAuth should be above; this is just a safeguard
  if (!roles.includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold">Access denied</h1>
          <p className="text-muted-foreground text-sm">
            This portal is not available for your account role.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
